import React, { useEffect, useState } from 'react';
import { getUsers, createUser, updateUser, deleteUser, getProjects } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Users as UsersIcon } from 'lucide-react';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    cpf_cnpj: '',
    email: '',
    phone: '',
    role: 'client',
    password: '',
    is_active: true,
    linked_projects: []
  });

  useEffect(() => {
    fetchUsers();
    fetchProjects();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await getUsers();
      setUsers(response.data);
    } catch (error) {
      toast.error('Erro ao carregar usuários');
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await getProjects();
      setProjects(response.data);
    } catch (error) {
      console.error('Erro ao carregar projetos:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await updateUser(editingUser.id, formData);
        toast.success('Usuário atualizado com sucesso!');
      } else {
        await createUser(formData);
        toast.success('Usuário criado com sucesso!');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar usuário');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({ ...user, password: '' });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este usuário?')) {
      try {
        await deleteUser(id);
        toast.success('Usuário excluído com sucesso!');
        fetchUsers();
      } catch (error) {
        toast.error('Erro ao excluir usuário');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      cpf_cnpj: '',
      email: '',
      phone: '',
      role: 'client',
      password: '',
      is_active: true,
      linked_projects: []
    });
    setEditingUser(null);
  };

  const getRoleLabel = (role) => {
    const labels = {
      administrator: 'Administrador',
      client: 'Cliente',
      service_provider: 'Fornecedor'
    };
    return labels[role] || role;
  };

  const toggleProjectLink = (projectId) => {
    setFormData(prev => ({
      ...prev,
      linked_projects: prev.linked_projects.includes(projectId)
        ? prev.linked_projects.filter(id => id !== projectId)
        : [...prev.linked_projects, projectId]
    }));
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-secondary font-bold uppercase tracking-tight text-slate-900">
            Usuários
          </h1>
          <p className="text-slate-600 font-primary mt-1">Gerencie usuários do sistema</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm w-full sm:w-auto" data-testid="add-user-button">
              <Plus className="h-4 w-4 mr-2" />
              Novo Usuário
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-secondary uppercase tracking-tight">
                {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="user-form">
              <div>
                <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                  Nome Completo / Razão Social *
                </Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="mt-1 rounded-sm"
                  data-testid="user-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                    CPF / CNPJ *
                  </Label>
                  <Input
                    value={formData.cpf_cnpj}
                    onChange={(e) => setFormData({ ...formData, cpf_cnpj: e.target.value })}
                    required
                    className="mt-1 rounded-sm"
                    data-testid="user-cpf-input"
                  />
                </div>
                <div>
                  <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                    Telefone *
                  </Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    className="mt-1 rounded-sm"
                    data-testid="user-phone-input"
                  />
                </div>
              </div>
              <div>
                <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                  Email *
                </Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="mt-1 rounded-sm"
                  data-testid="user-email-input"
                />
              </div>
              <div>
                <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                  Senha {editingUser ? '(deixe em branco para não alterar)' : '*'}
                </Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                  className="mt-1 rounded-sm"
                  data-testid="user-password-input"
                />
              </div>
              <div>
                <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                  Tipo de Usuário *
                </Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="administrator">Administrador</SelectItem>
                    <SelectItem value="client">Cliente</SelectItem>
                    <SelectItem value="service_provider">Fornecedor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.role === 'client' && (
                <div>
                  <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500 mb-2 block">
                    Projetos Vinculados
                  </Label>
                  <div className="space-y-2 max-h-40 overflow-y-auto border border-slate-200 rounded-sm p-3">
                    {projects.map(project => (
                      <div key={project.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`project-${project.id}`}
                          checked={formData.linked_projects.includes(project.id)}
                          onCheckedChange={() => toggleProjectLink(project.id)}
                          data-testid={`project-checkbox-${project.id}`}
                        />
                        <label htmlFor={`project-${project.id}`} className="text-sm text-slate-700 cursor-pointer">
                          {project.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => {
                  setIsDialogOpen(false);
                  resetForm();
                }}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm" data-testid="submit-user-button">
                  {editingUser ? 'Atualizar' : 'Criar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Users List */}
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
        {/* Mobile: card list */}
        <div className="md:hidden divide-y divide-slate-200">
          {users.map((user) => (
            <div key={user.id} className="p-4 space-y-2" data-testid="user-row">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-900 break-words">{user.name}</p>
                  <p className="text-xs text-slate-600 break-all">{user.email}</p>
                </div>
                <span className={`shrink-0 px-2 py-1 rounded-sm text-xs font-medium ${
                  user.is_active ? 'text-emerald-600 bg-emerald-50' : 'text-slate-600 bg-slate-100'
                }`}>
                  {user.is_active ? 'Ativo' : 'Inativo'}
                </span>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
                <span className="break-words"><strong>CPF/CNPJ:</strong> {user.cpf_cnpj || '—'}</span>
                <span><strong>Tipo:</strong> {getRoleLabel(user.role)}</span>
              </div>
              <div className="flex gap-2 pt-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleEdit(user)}
                  className="rounded-sm flex-1"
                  data-testid="edit-user-button"
                >
                  <Edit className="h-3 w-3 mr-1" /> Editar
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleDelete(user.id)}
                  className="rounded-sm"
                  data-testid="delete-user-button"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop: table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full" data-testid="users-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-left font-secondary font-semibold uppercase tracking-wider text-xs text-slate-500">
                  Nome
                </th>
                <th className="px-6 py-3 text-left font-secondary font-semibold uppercase tracking-wider text-xs text-slate-500">
                  Email
                </th>
                <th className="px-6 py-3 text-left font-secondary font-semibold uppercase tracking-wider text-xs text-slate-500">
                  CPF/CNPJ
                </th>
                <th className="px-6 py-3 text-left font-secondary font-semibold uppercase tracking-wider text-xs text-slate-500">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left font-secondary font-semibold uppercase tracking-wider text-xs text-slate-500">
                  Status
                </th>
                <th className="px-6 py-3 text-right font-secondary font-semibold uppercase tracking-wider text-xs text-slate-500">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-slate-50" data-testid="user-row-desktop">
                  <td className="px-6 py-4 text-sm font-medium text-slate-900">{user.name}</td>
                  <td className="px-6 py-4 text-sm text-slate-600">{user.email}</td>
                  <td className="px-6 py-4 text-sm text-slate-600">{user.cpf_cnpj}</td>
                  <td className="px-6 py-4 text-sm text-slate-600">{getRoleLabel(user.role)}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-sm text-xs font-medium ${
                      user.is_active ? 'text-emerald-600 bg-emerald-50' : 'text-slate-600 bg-slate-100'
                    }`}>
                      {user.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(user)}
                      className="rounded-sm"
                      data-testid="edit-user-button-desktop"
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(user.id)}
                      className="rounded-sm"
                      data-testid="delete-user-button-desktop"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {users.length === 0 && (
          <div className="text-center py-12">
            <UsersIcon className="h-12 w-12 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
            <p className="text-slate-600">Nenhum usuário cadastrado</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Users;
